import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM, Text, Button, Icon, Spinner } from "components/atoms";
import { AIReviewEvaluationGroup, AIReviewEvaluation } from "common/models/types";
import { evaluationGroupApi } from "services/api/aiReviewTest/evaluationGroupApi";
import { ToastManager } from "components/organisms/CommonUI";
import { WaitingManager } from "components/organisms/CommonUI/WaitingPopup/WaitingManager";
import Tabs from "components/molecules/Tabs";
import { downloadEvaluationGroupAsZip } from "utils/evaluationExportUtil";

const Container = styled.div`
  padding: 24px;
  height: 100%;
  overflow-y: auto;
`;

const TabContent = styled.div`
  margin-top: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MenuButton = styled(Button)`
  padding: 8px;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: white;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  min-width: 180px;
  z-index: 9999;
`;

const DropdownMenuItem = styled.div`
  padding: 12px 16px;
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`;

const Title = styled(Text)`
  font-size: 20px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;


const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  margin-bottom: 16px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding: 16px;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 8px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled(Text)`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

const InfoValue = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  font-weight: 500;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div<{ color?: string }>`
  padding: 20px;
  background: white;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  ${({ color }) =>
    color &&
    `
    border-color: ${color};
    background: ${color}10;
  `}
`;

const StatValue = styled(Text)<{ color?: string }>`
  font-size: 24px;
  font-weight: 700;
  color: ${({ color }) => color || COLOR_SYSTEM.get("Gray")?.[900]};
  margin-bottom: 8px;
`;

const StatLabel = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

const ConfigBox = styled.div`
  padding: 16px;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  margin-bottom: 16px;
`;

const ConfigContent = styled.pre`
  font-family: "Courier New", monospace;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 200px;
  overflow-y: auto;
`;

const EvaluationTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  overflow: hidden;
`;

const Thead = styled.thead`
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};

  &:last-child {
    border-bottom: none;
  }

  tbody &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[10]};
    transition: background-color 0.2s ease;
  }
`;

const Th = styled.th`
  padding: 12px;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

const Td = styled.td`
  padding: 12px;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

const ScoreCell = styled(Td)<{ score: number }>`
  font-weight: 600;
  color: ${({ score }) =>
    score >= 80
      ? COLOR_SYSTEM.get("Green")?.[600]
      : score >= 60
      ? COLOR_SYSTEM.get("Yellow")?.[700]
      : COLOR_SYSTEM.get("Red")?.[600]};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
`;

const EmptyMessage = styled(Text)`
  text-align: center;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  padding: 40px;
`;

const ViewButton = styled(Button)`
  font-size: 12px;
  padding: 4px 8px;
`;

interface EvaluationGroupDetailProps {
  group: AIReviewEvaluationGroup;
  onClose: () => void;
  onUpdate?: (updatedGroup: AIReviewEvaluationGroup) => void;
}

const EvaluationGroupDetail: React.FC<EvaluationGroupDetailProps> = ({ group, onClose, onUpdate }) => {
  const [evaluations, setEvaluations] = useState<AIReviewEvaluation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("info");
  const [showDropdown, setShowDropdown] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchEvaluations();
  }, [group.id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const fetchEvaluations = async () => {
    if (!group.id) return;

    try {
      setIsLoading(true);
      const response = await evaluationGroupApi.findEvaluations(group.id, { limit: 100 });
      if (response.data.error === 0 && response.data.data) {
        setEvaluations(response.data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch evaluations:", error);
      ToastManager.show({
        content: "평가 목록을 불러오는데 실패했습니다.",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatScore = (score: number | null | undefined) => {
    if (score === null || score === undefined) return "-";
    return Math.round(score).toString();
  };

  const formatCost = (cost: number | null | undefined) => {
    if (cost === null || cost === undefined) return "-";
    const numCost = typeof cost === 'number' ? cost : parseFloat(cost as any);
    if (isNaN(numCost)) return "-";
    return `$${numCost.toFixed(4)}`;
  };

  const formatDateTime = (dateString: any) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("ko-KR", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleViewEvaluation = (evaluation: AIReviewEvaluation) => {
    // 화면 크기의 90%를 차지하도록 설정, 최대 1920x1080
    const width = Math.min(1920, window.screen.width * 0.9);
    const height = Math.min(1080, window.screen.height * 0.9);
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    window.open(
      `/admin/ai-reviews/${evaluation.aiReviewId}/popup/evaluation/${evaluation.id}`,
      `evaluation-${evaluation.id}`,
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
  };

  const handleDownload = async () => {
    if (isDownloading) return;
    setShowDropdown(false);

    try {
      setIsDownloading(true);
      WaitingManager.show({ message: "평가 데이터 다운로드 준비 중..." });

      await downloadEvaluationGroupAsZip(group, evaluations, (message) => {
        WaitingManager.updateMessage(message);
      });

      ToastManager.show({
        content: `${evaluations.length}건의 평가 데이터를 다운로드했습니다.`,
        duration: 3000,
      });
    } catch (error) {
      console.error("Failed to download evaluations:", error);
      ToastManager.show({
        content: "다운로드에 실패했습니다.",
        duration: 3000,
      });
    } finally {
      setIsDownloading(false);
      WaitingManager.hide();
    }
  };

  const handleRecalculateStats = async () => {
    if (!group.id) return;

    try {
      setIsRecalculating(true);
      const response = await evaluationGroupApi.recalculateStats(group.id);
      if (response.data.error === 0 && response.data.data) {
        ToastManager.show({
          content: "통계가 성공적으로 다시 계산되었습니다.",
          duration: 3000,
        });
        // 업데이트된 그룹 데이터를 부모 컴포넌트에 전달
        if (onUpdate && response.data.data) {
          onUpdate(response.data.data);
        }
      }
    } catch (error) {
      console.error("Failed to recalculate stats:", error);
      ToastManager.show({
        content: "통계 재계산에 실패했습니다.",
        duration: 3000,
      });
    } finally {
      setIsRecalculating(false);
      setShowDropdown(false);
    }
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <Title>평가 그룹 상세</Title>
          <div style={{ position: "relative" }} ref={dropdownRef}>
            <MenuButton
              size="Small"
              type="Icon"
              variant="Ghost"
              color="Primary"
              onClick={() => setShowDropdown(!showDropdown)}
              icon="Dots Horizontal"
              disabled={isRecalculating || isDownloading}
            />
            {showDropdown && (
              <DropdownMenu>
                <DropdownMenuItem onClick={handleDownload}>전체 다운로드 (.md)</DropdownMenuItem>
                <DropdownMenuItem onClick={handleRecalculateStats}>통계 계산하기</DropdownMenuItem>
              </DropdownMenu>
            )}
          </div>
        </HeaderLeft>
        <HeaderRight>
          <Button
            size="Small"
            type="Icon"
            variant="Ghost"
            color="Primary"
            onClick={onClose}
            icon="X"
          />
        </HeaderRight>
      </Header>

      {/* 탭 */}
      <Tabs
        tabs={[
          { id: "info", label: "그룹 정보" },
          { id: "evaluations", label: `개별 평가 결과 (${evaluations.length})` },
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <TabContent>
        {activeTab === "info" ? (
          <>
            {/* 통계 카드 */}
            <Section>
              <StatsGrid>
                <StatCard color={COLOR_SYSTEM.get("Blue")?.[600]}>
                  <StatValue color={COLOR_SYSTEM.get("Blue")?.[600]}>
                    {formatScore(group.averageScore)}
                  </StatValue>
                  <StatLabel>평균 점수</StatLabel>
                </StatCard>
                <StatCard color={COLOR_SYSTEM.get("Green")?.[600]}>
                  <StatValue color={COLOR_SYSTEM.get("Green")?.[600]}>
                    {group.totalExecutions > 0
                      ? `${Math.round((group.successCount / group.totalExecutions) * 100)}%`
                      : "-"}
                  </StatValue>
                  <StatLabel>성공률</StatLabel>
                </StatCard>
                <StatCard color={COLOR_SYSTEM.get("Purple")?.[600]}>
                  <StatValue color={COLOR_SYSTEM.get("Purple")?.[600]}>
                    {formatCost(group.avgCost)}
                  </StatValue>
                  <StatLabel>평균 비용</StatLabel>
                </StatCard>
              </StatsGrid>
            </Section>

            {/* 그룹 정보 */}
            <Section>
              <SectionTitle>그룹 정보</SectionTitle>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>테스트 그룹 키</InfoLabel>
                  <InfoValue style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                    {group.testGroupKey.split("/").join("\n")}
                  </InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>평가 그룹 키</InfoLabel>
                  <InfoValue>{group.evaluationGroupKey}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>평가 모델</InfoLabel>
                  <InfoValue>{group.evaluationModel || "기본 모델"}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>평가 프롬프트 ID</InfoLabel>
                  <InfoValue>{group.evaluationPromptId}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>총 실행 횟수</InfoLabel>
                  <InfoValue>{group.totalExecutions}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>성공/실패</InfoLabel>
                  <InfoValue>
                    {group.successCount} / {group.failedCount}
                  </InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>점수 범위</InfoLabel>
                  <InfoValue>
                    {formatScore(group.minScore)} ~ {formatScore(group.maxScore)}
                  </InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>표준편차</InfoLabel>
                  <InfoValue>{formatScore(group.stdDeviationScore)}</InfoValue>
                </InfoItem>
              </InfoGrid>
            </Section>

            {/* AI Review 요청 설정 */}
            <Section>
              <SectionTitle>AI Review 요청 설정</SectionTitle>
              <ConfigBox>
                <ConfigContent>{JSON.stringify(group.aiReviewRequest, null, 2)}</ConfigContent>
              </ConfigBox>
            </Section>
          </>
        ) : (
          /* 개별 평가 결과 */
          <Section>
            {isLoading ? (
              <LoadingContainer>
                <Spinner size="Medium" />
              </LoadingContainer>
            ) : evaluations.length === 0 ? (
              <EmptyMessage>평가 결과가 없습니다.</EmptyMessage>
            ) : (
              <EvaluationTable>
                <Thead>
                  <Tr>
                    <Th>평가 ID</Th>
                    <Th>AI Review ID</Th>
                    <Th>환자번호</Th>
                    <Th>점수</Th>
                    <Th>상태</Th>
                    <Th>평가 시점</Th>
                    <Th>생성일시</Th>
                    <Th>작업</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {evaluations.map((evaluation) => (
                    <Tr key={evaluation.id}>
                      <Td>{evaluation.id}</Td>
                      <Td>{evaluation.aiReviewId}</Td>
                      <Td>{evaluation.patientId || '-'}</Td>
                      <ScoreCell score={evaluation.score || 0}>
                        {formatScore(evaluation.score)}
                      </ScoreCell>
                      <Td>{evaluation.status}</Td>
                      <Td>{formatDateTime(evaluation.evaluatedAt || evaluation.createdAt)}</Td>
                      <Td>{formatDateTime(evaluation.createdAt)}</Td>
                      <Td>
                        <ViewButton
                          size="Small"
                          type="Text"
                          variant="Outlined"
                          color="Primary"
                          onClick={() => handleViewEvaluation(evaluation)}
                        >
                          상세보기
                        </ViewButton>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </EvaluationTable>
            )}
          </Section>
        )}
      </TabContent>
    </Container>
  );
};

export default EvaluationGroupDetail;
